import EnquiryForm from "../components/common/EnquiryForm";

export default function PrivacyPolicy() {
  return (
    <main className="pt-16 md:pt-20">
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl font-semibold text-gray-900 mb-8">Privacy Policy</h1>

        <p className="text-gray-600 leading-relaxed mb-8">
          Artistic Spaces understands that your privacy is important and that you care about how your personal data
          is used. We respect and value the privacy of all of our clients and stakeholders and others with whom we
          have a relationship. We will only collect and use personal data in ways that are described here, and in a
          way that is consistent with our obligations and your rights under the law.
        </p>

        <div className="flex flex-col gap-8">
          <Section title="Information About Us">
            <p>
              Artistic Spaces registered in England under company number 07150124. Registered address and Head
              Office: 20a Ironmonger Lane, London, United Kingdom, EC2V 8EP. Main Switchboard telephone number:
              0203 3024266.
            </p>
          </Section>

          <Section title="What Does This Notice Cover?">
            <p>
              This Privacy Notice explains how we use your personal data: how it is collected, how it is held, and
              how it is processed. It also explains your rights under the law relating to your personal data.
            </p>
          </Section>

          <Section title="What Is Personal Data?">
            <p>
              Personal data is defined by the General Data Protection Regulation (EU Regulation 2016/679) (the
              "GDPR") as 'any information relating to an identifiable person who can be directly or indirectly
              identified in particular by reference to an identifier'. Personal data is, in simpler terms, any
              information about you that enables you to be identified.
            </p>
          </Section>

          <Section title="What Are Your Rights?">
            <p className="mb-3">Under the GDPR, you have the following rights, which we will always work to uphold:</p>
            <ul className="flex flex-col gap-2">
              {[
                "The right to be informed about our collection and use of your personal data.",
                "The right to access the personal data we hold about you.",
                "The right to have your personal data rectified if any of your personal data held by us is inaccurate or incomplete.",
                "The right to ask us to delete or otherwise dispose of any of your personal data that we have.",
                "The right to restrict (i.e. prevent) the processing of your personal data.",
                "The right to object to us using your personal data for a particular purpose or purposes.",
                "Rights relating to automated decision-making and profiling. We do not use your personal data in this way.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                  <span className="text-teal-600 mt-0.5">—</span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-gray-600">
              If you have any cause for complaint about our use of your personal data, you have the right to lodge
              a complaint with the Information Commissioner's Office.
            </p>
          </Section>

          <Section title="What Personal Data Do We Collect?">
            <p className="mb-3">We may collect some or all of the following personal data:</p>
            <ul className="flex flex-col gap-1">
              {["Name", "Address", "Email address", "Telephone and mobile numbers", "Business name", "Job title", "Profession", "Banking and payment information"].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                  <span className="text-teal-600 mt-0.5">—</span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-gray-600">
              On occasion personal data may be obtained from third parties, such as managing agents, referees,
              reference checking agencies or others providing services to us.
            </p>
          </Section>

          <Section title="How Do We Use Your Personal Data?">
            <p className="mb-3">
              Under the GDPR, we must always have a lawful basis for using personal data. Your personal data may
              be used for one or more of the following purposes:
            </p>
            <ul className="flex flex-col gap-1">
              {["Property management", "Property transactions", "Contract management and/or performance", "Receiving or making payments", "Legal and regulatory compliance", "Legal proceedings", "Communicating with you"].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                  <span className="text-teal-600 mt-0.5">—</span>
                  {item}
                </li>
              ))}
            </ul>
          </Section>

          <Section title="How Long Will We Keep Your Personal Data?">
            <p>
              We will not keep your personal data for any longer than is necessary in light of the reason(s) for
              which it was first collected. This will usually be for the duration of our relationship and then for
              a period of six years thereafter.
            </p>
          </Section>

          <Section title="How and Where Do We Store or Transfer Your Personal Data?">
            <p>
              We will only store or transfer your personal data in the UK. This means that it will be fully
              protected under the GDPR.
            </p>
          </Section>

          <Section title="Do We Share Your Personal Data?">
            <p>
              We may sometimes contract with third parties to supply services on our behalf. These may include
              payment processing, tenancy or property management, contract works or professional services and
              advice. In some cases, those third parties may require access to some or all of your personal data
              that we hold. We will take steps to ensure that your personal data is handled safely, securely, and
              in accordance with your rights, our obligations, and the third party's obligations under the law.
            </p>
          </Section>

          <Section title="How Can You Access Your Personal Data?">
            <p>
              If you want to know what personal data we have about you, you can ask us for details of that
              personal data and or a copy of it (where any such personal data is held). This is known as a
              'subject access request'. All subject access requests should be made in writing and sent to{" "}
              <a href="mailto:hello@artistic-spaces.co.uk" className="text-teal-700 hover:underline">
                hello@artistic-spaces.co.uk
              </a>
              , together with proof of your identity. We will respond within one month of receiving your request.
            </p>
          </Section>

          <Section title="How to Contact Us">
            <p>
              To contact us about anything to do with your personal data and data protection, please email:{" "}
              <a href="mailto:hello@artistic-spaces.co.uk" className="text-teal-700 hover:underline">
                hello@artistic-spaces.co.uk
              </a>
            </p>
          </Section>

          <Section title="Changes to This Privacy Notice">
            <p>
              We may change this Privacy Notice from time to time. Any changes will be made available on this
              website.
            </p>
            <p className="mt-3 text-sm text-gray-500">March 2022</p>
          </Section>
        </div>
      </section>

      <EnquiryForm pageUri="https://artistic-spaces.co.uk/privacy-policy" pageName="Privacy Policy" />
    </main>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-3">{title}</h2>
      <div className="text-gray-600 text-sm leading-relaxed">{children}</div>
    </div>
  );
}